import React from 'react';

/**
 * The Heimdl mark: a fake barcode whose lemon crossbar makes it an H.
 *
 * ⚠ The bar widths are INVENTED and must never form a valid barcode. If you change any `x`/`width`
 * below, scan the rendered mark with a barcode app and confirm nothing parses — a scannable logo
 * could be read into a customer's own catalogue as a phantom item.
 *
 * The bars take `currentColor`, so set the parent's text colour (white on petrol, petrol on white).
 * The crossbar stays lemon in every context, positive or reversed — it is the constant. Never
 * recolour it, never rotate or stretch the mark, never add a container shape.
 */

const LEMON = '#F2D02C';

const BARS = {
  // guard-bar: the default, used almost everywhere
  guard: [
    { x: 22, y: 16, width: 3, height: 76 }, { x: 27, y: 16, width: 2, height: 68 },
    { x: 31, y: 16, width: 4, height: 68 }, { x: 36, y: 16, width: 2, height: 76 },
    { x: 62, y: 16, width: 2, height: 76 }, { x: 66, y: 16, width: 4, height: 68 },
    { x: 72, y: 16, width: 2, height: 68 }, { x: 76, y: 16, width: 2, height: 76 },
  ],
  // numerals: covers, cards, print — anything above ~48px
  numerals: [
    { x: 22, y: 10, width: 3, height: 76 }, { x: 27, y: 10, width: 2, height: 68 },
    { x: 31, y: 10, width: 4, height: 68 }, { x: 36, y: 10, width: 2, height: 76 },
    { x: 62, y: 10, width: 2, height: 76 }, { x: 66, y: 10, width: 4, height: 68 },
    { x: 72, y: 10, width: 2, height: 68 }, { x: 76, y: 10, width: 2, height: 76 },
  ],
  // plain: embroidery, stamps — anything that cannot hold detail
  plain: [
    { x: 22, y: 18, width: 3, height: 68 }, { x: 27, y: 18, width: 2, height: 68 },
    { x: 31, y: 18, width: 4, height: 68 }, { x: 36, y: 18, width: 2, height: 68 },
    { x: 62, y: 18, width: 2, height: 68 }, { x: 66, y: 18, width: 4, height: 68 },
    { x: 72, y: 18, width: 2, height: 68 }, { x: 76, y: 18, width: 2, height: 68 },
  ],
};

const CROSSBAR = { guard: 44, numerals: 38, plain: 46 };

/**
 * @param {object} props
 * @param {number} [props.size]        rendered width/height in px
 * @param {boolean} [props.showText]   render the "Heimdl" wordmark beside the mark
 * @param {'guard'|'numerals'|'plain'} [props.variant]
 * @param {string} [props.className]   extra classes on the wrapper
 */
export const Logo = ({ size = 40, showText = false, variant = 'guard', className = '' }) => {
  const bars = BARS[variant] || BARS.guard;
  return (
    <div className={`flex items-center gap-3 ${className}`.trim()}>
      <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label="Heimdl">
        <g fill="currentColor">
          {bars.map((b) => <rect key={`${b.x}-${b.y}`} {...b} />)}
        </g>
        <rect x="38" y={CROSSBAR[variant] || CROSSBAR.guard} width="24" height="12" fill={LEMON} />
        {variant === 'numerals' && (
          <g fill="currentColor" fontFamily="Archivo, Arial, sans-serif" fontSize="9" letterSpacing="1" textAnchor="middle">
            <text x="30" y="97">025</text>
            <text x="70" y="97">HEI</text>
          </g>
        )}
      </svg>
      {showText && (
        <span className="font-display text-display leading-none">Heimdl</span>
      )}
    </div>
  );
};

export default Logo;
